// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Source attribution and AI limitation disclosure.
 *
 * `ui/docs/Implementation.md` §5 and `docs/05-CODING-STANDARDS.md` §26 require
 * citations and material AI limitations to be displayed. `RMP-004` adds that a
 * copilot must distinguish authoritative records from generated suggestions and
 * express uncertainty, so limitations are rendered as a prominent note, not
 * hidden behind a tooltip.
 *
 * Citation links follow the same restricted policy as sanitised Markdown:
 * `https:` only, opened in a new context with `noopener noreferrer nofollow`.
 */

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import type { Citation } from '../api-clients/contracts';

const HTTPS_ONLY = /^https:/i;

/** Props for {@link CitationList}. */
export interface CitationListProps {
  /** Citations supporting the displayed content. */
  readonly citations: readonly Citation[];
  /** Heading for the citation block. */
  readonly heading?: string;
}

/** Render supporting sources for AI-assisted content. */
export function CitationList({ citations, heading = 'Sources' }: CitationListProps): ReactNode {
  if (citations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No supporting sources were provided for this content.
      </Typography>
    );
  }
  return (
    <Box>
      <Typography variant="h3" component="p" gutterBottom>
        {heading}
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 3 }}>
        {citations.map((citation) => {
          const uri = citation.source_uri;
          const linkable = uri !== null && HTTPS_ONLY.test(uri);
          return (
            <li key={citation.citation_id}>
              <Typography variant="body2" component="span">
                {linkable ? (
                  <Link href={uri} target="_blank" rel="noopener noreferrer nofollow">
                    {citation.source_title}
                  </Link>
                ) : (
                  citation.source_title
                )}
                {citation.page_number === null ? null : ` (page ${String(citation.page_number)})`}
              </Typography>
              {citation.snippet === null ? null : (
                <Typography variant="body2" color="text.secondary">
                  {citation.snippet}
                </Typography>
              )}
            </li>
          );
        })}
      </Box>
    </Box>
  );
}

/** Props for {@link AiLimitations}. */
export interface AiLimitationsProps {
  /** Material limitations the reviewer must be told about. */
  readonly limitations: readonly string[];
}

/** Disclose material AI limitations alongside generated content. */
export function AiLimitations({ limitations }: AiLimitationsProps): ReactNode {
  if (limitations.length === 0) {
    return null;
  }
  return (
    <Alert severity="info" role="note" sx={{ my: 2 }}>
      <AlertTitle>AI assistance limitations</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        {limitations.map((limitation) => (
          <li key={limitation}>
            <Typography variant="body2">{limitation}</Typography>
          </li>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mt: 1 }}>
        This information supports your decision. It is not an institutional decision and does not
        replace the authoritative record.
      </Typography>
    </Alert>
  );
}
